import {
  SceneTypeDto,
  type ElementDto,
  type TextElementDto,
} from "@repo/api/overlays";
import {
  getAllElements,
  getElementsByScene,
  getResolution,
} from "@repo/api/utils/composition";
import type { CompositionDto } from "@repo/api/editor";
import {
  findTopmostAt,
  getElementBox,
  isPointInBox,
  measureText,
  Point,
  Size,
} from "@/features/Editor/Canvas/canvas-geometry";
import {
  findElementTiming,
  getCompositionTimings,
} from "@repo/video-engine-i2v/utils/timing";
import {
  getLoadedFontFamily,
  loadCustomFont,
  subscribeToFontLoads,
} from "@repo/video-engine-i2v/utils/font";
import { getTextLayoutStyle } from "@repo/video-engine-i2v/utils/text-layout";
import type { EditorStore } from "../editor-store/editor-store";
import type { EditorService } from "../editor-store/editor-service";
import { ElementOrdering, getReorderedElements } from "./canvas-layer-order";
import { createElementCopy, getDuplicatedElements } from "./canvas-duplicate";
import type { Set as StoreSet, Get as StoreGet } from "./canvas-store";
import type { CanvasElement } from "./canvas-state";
import {
  Gesture,
  GestureOutput,
  isBeyondDragThreshold,
  Modifiers,
  PressContext,
} from "./canvas-gesture";
import { createMarqueeGesture } from "./canvas-marquee";
import { createMoveGesture } from "./canvas-move";
import { createNudge } from "./canvas-nudge";
import { getFullscreenBox } from "./canvas-fullscreen";
import { AlignType, getAlignOffsets } from "./canvas-align";
import { Clipboard, getPastePosition } from "./canvas-clipboard";
import { createResizeGesture, ResizeHandle } from "./canvas-resize";
import { getSelectionBox, haveSameMembers } from "./canvas-selection";

const NUDGE_COMMIT_DELAY_MS = 500;

type Nudge = {
  move: (offset: Point) => GestureOutput;
  offset: Point;
  commitTimeoutId?: ReturnType<typeof setTimeout>;
};

type Press = {
  kind: "move" | "resize" | "marquee";
  gesture: Gesture;
  selectedIdsAtStart: string[];
  start: Point;
  lastPoint: Point;
  scale: number;
  isDragging: boolean;
};

/**
 * Derives the canvas state from the editor store. Element data stays in the editor store;
 * the canvas only holds the boxes of elements visible at the current frame.
 *
 * syncCanvasState runs on composition/font changes, frame changes and finished font loads:
 * 1. loadFonts starts loading every text element's font. The font registry
 *    loads each font once and notifies when one finishes, which runs syncCanvasState again.
 * 2. refreshCanvasElements synchronously computes the boxes. Sized elements use their
 *    stored size; text is measured with its loaded font, or skipped until it has loaded.
 */
export class CanvasService {
  constructor(set: StoreSet, get: StoreGet) {
    this.set = set;
    this.get = get;
  }

  private set: StoreSet;
  private get: StoreGet;

  private editorStore: EditorStore | null = null;
  private unsubEditorStore: () => void = () => {};
  private unsubFontLoads: () => void = () => {};
  private press: Press | null = null;
  private nudge: Nudge | null = null;
  private clipboard: Clipboard | null = null;

  initialize = (editorStore: EditorStore) => {
    this.editorStore = editorStore;

    this.unsubEditorStore = editorStore.subscribe((state, previousState) => {
      const composition = state.video?.compositionJson;
      const previousComposition = previousState.video?.compositionJson;

      if (composition !== previousComposition) {
        this.handleCompositionChange(composition, previousComposition);
      }

      const { editingTextId } = this.get();

      if (editingTextId && !state.selectedElementIds.includes(editingTextId)) {
        this.stopTextEditing();
      }

      const currentElement = state.overlaysTabState.currentElement;

      // An element opened in the panel, e.g. a newly added one, becomes the selection.
      if (
        currentElement &&
        currentElement !== previousState.overlaysTabState.currentElement &&
        !state.selectedElementIds.includes(currentElement.externalId)
      ) {
        this.setSelectedElementIds([currentElement.externalId]);
      }

      if (
        composition !== previousComposition ||
        state.animatedImages !== previousState.animatedImages ||
        state.compositionFonts !== previousState.compositionFonts
      ) {
        this.syncCanvasState();
      }
    });

    this.unsubFontLoads = subscribeToFontLoads(this.syncCanvasState);

    this.syncCanvasState();
  };

  destroy = () => {
    this.unsubEditorStore();
    this.unsubFontLoads();
    this.editorStore = null;
  };

  setFrame = (frame: number) => {
    if (frame === this.get().frame) {
      return;
    }

    this.set((state) => {
      state.frame = frame;
    });
    this.syncCanvasState();
  };

  private measureTextElement = (element: TextElementDto): Size | null => {
    const { fontExternalId, fontWeight, fontStyle } = element.typography;
    const font = this.getFont(fontExternalId);

    if (!font) {
      return null;
    }

    const fontFamily = getLoadedFontFamily(
      font.externalId,
      fontWeight,
      fontStyle,
    );

    if (!fontFamily) {
      return null;
    }

    return measureText(
      element.text,
      getTextLayoutStyle(element.typography, fontFamily),
    );
  };

  pointerDown = (
    point: Point,
    modifiers: Modifiers,
    scale: number,
    handle: ResizeHandle | null,
  ) => {
    const composition = this.editorStore?.getState().video?.compositionJson;

    if (!composition) {
      return;
    }

    const { canvasElements, editingTextId } = this.get();

    if (editingTextId) {
      const editedBox = canvasElements.find(
        ({ externalId }) => externalId === editingTextId,
      )?.box;

      // Presses inside the edited text belong to the text field.
      if (editedBox && isPointInBox(point, editedBox)) {
        return;
      }

      this.stopTextEditing();
    }

    this.commitNudge();

    const selectedIds = this.getSelectedElementIds();
    const context: PressContext = {
      start: point,
      modifiers,
      canvasElements,
      elements: Object.fromEntries(
        getAllElements(composition).map(({ element }) => [
          element.externalId,
          element,
        ]),
      ),
      selectedIds,
      resolution: getResolution(composition.aspectRatio),
      scale,
    };
    const { kind, gesture } = this.createGesture(context, handle);

    this.press = {
      kind,
      gesture,
      selectedIdsAtStart: selectedIds,
      start: point,
      lastPoint: point,
      scale,
      isDragging: false,
    };
    this.set((state) => {
      state.activeHandle = handle;
      state.hoveredId = null;
    });
    this.applyGestureOutput(gesture.press(modifiers));
  };

  // Double-clicking a text element edits it in place.
  doubleClick = (point: Point) => {
    const target = findTopmostAt(this.get().canvasElements, point);

    if (!this.press && target) {
      this.startTextEditing(target.externalId);
    }
  };

  // Enter edits the single selected text element.
  editSelectedText = () => {
    const [selectedId, ...otherIds] = this.getSelectedElementIds();

    return (
      !this.isBusy() &&
      selectedId !== undefined &&
      otherIds.length === 0 &&
      this.startTextEditing(selectedId)
    );
  };

  // Only for a text element visible at the current frame.
  private startTextEditing = (externalId: string) => {
    const element = getAllElements(
      this.editorStore?.getState().video?.compositionJson,
    ).find(({ element }) => element.externalId === externalId)?.element;
    const isVisible = this.get().canvasElements.some(
      (canvasElement) => canvasElement.externalId === externalId,
    );

    if (element?.type !== "TEXT" || !isVisible) {
      return false;
    }

    this.commitNudge();
    this.setSelectedElementIds([externalId]);
    this.set((state) => {
      state.editingTextId = externalId;
    });

    return true;
  };

  // Typing in the text field; the box follows the measured text.
  editText = (text: string) => {
    const { editingTextId, canvasElements } = this.get();
    const element = getAllElements(
      this.editorStore?.getState().video?.compositionJson,
    ).find(({ element }) => element.externalId === editingTextId)?.element;
    const box = canvasElements.find(
      ({ externalId }) => externalId === editingTextId,
    )?.box;

    if (!editingTextId || element?.type !== "TEXT" || !box) {
      return;
    }

    const size = this.measureTextElement({ ...element, text });

    if (!size) {
      return;
    }

    this.set((state) => {
      state.drafts = {
        [editingTextId]: { x: box.x, y: box.y, w: size.w, h: size.h, text },
      };
    });
  };

  // Text left empty is removed instead of committed.
  stopTextEditing = () => {
    const { editingTextId, drafts } = this.get();

    if (!editingTextId) {
      return;
    }

    // Cleared before committing so the commit isn't seen as an outside change.
    this.set((state) => {
      state.editingTextId = null;
    });

    if (drafts[editingTextId]?.text?.trim() === "") {
      this.deleteElement(editingTextId);
    } else {
      this.commitDrafts();
    }

    this.resetTemporaryState();
  };

  private deleteElement = (externalId: string) => {
    const editorService = this.editorStore?.getState().service;
    const sceneType = getAllElements(
      this.editorStore?.getState().video?.compositionJson,
    ).find(({ element }) => element.externalId === externalId)?.sceneType;

    if (editorService && sceneType) {
      editorService.deleteElement(externalId, sceneType);
    }
  };

  private cancelTextEditing = () => {
    if (this.get().editingTextId) {
      this.set((state) => {
        state.editingTextId = null;
      });
      this.resetTemporaryState();
    }
  };

  // Pointer moves without a press; point is null when the pointer isn't over the canvas.
  hover = (point: Point | null) => {
    if (this.press) {
      return;
    }

    const hoveredId = point
      ? (findTopmostAt(this.get().canvasElements, point)?.externalId ?? null)
      : null;

    if (hoveredId !== this.get().hoveredId) {
      this.set((state) => {
        state.hoveredId = hoveredId;
      });
    }
  };

  // A handle resizes; an element or the selection box moves; empty canvas draws a marquee.
  private createGesture = (
    context: PressContext,
    handle: ResizeHandle | null,
  ): Pick<Press, "kind" | "gesture"> => {
    if (handle) {
      return { kind: "resize", gesture: createResizeGesture(context, handle) };
    }

    const { start, canvasElements, selectedIds } = context;
    const target = findTopmostAt(canvasElements, start);
    const selectionBox = getSelectionBox(canvasElements, selectedIds);

    if (target || (selectionBox && isPointInBox(start, selectionBox))) {
      return {
        kind: "move",
        gesture: createMoveGesture(context, target?.externalId ?? null),
      };
    }

    return { kind: "marquee", gesture: createMarqueeGesture(context) };
  };

  pointerMove = (point: Point, modifiers: Modifiers) => {
    const press = this.press;

    if (!press) {
      return;
    }

    press.lastPoint = point;

    if (
      !press.isDragging &&
      !isBeyondDragThreshold(press.start, point, press.scale)
    ) {
      return;
    }

    press.isDragging = true;
    this.drag(press, modifiers);
  };

  modifiersChange = (modifiers: Modifiers) => {
    if (this.press?.isDragging) {
      this.drag(this.press, modifiers);
    }
  };

  pointerUp = (modifiers: Modifiers) => {
    const press = this.press;

    if (!press) {
      return;
    }

    // Cleared before committing so the commit isn't seen as an outside change.
    this.press = null;

    if (press.isDragging) {
      this.commitDrafts();
    } else {
      this.applyGestureOutput(press.gesture.click(modifiers));
    }

    this.endPress();

    const isSelectionChanged = !haveSameMembers(
      press.selectedIdsAtStart,
      this.getSelectedElementIds(),
    );

    // Clicks and presses that picked other elements update the panel; moving or resizing
    // what was already selected keeps it as it is.
    if (press.kind !== "resize" && (!press.isDragging || isSelectionChanged)) {
      this.syncPanelWithSelection();
    }
  };

  cancelGesture = () => {
    if (this.press) {
      this.endPress();
    }
  };

  // Moves the selection by the arrow keys; returns whether the key was used.
  nudgeSelection = (dx: number, dy: number) => {
    const composition = this.editorStore?.getState().video?.compositionJson;

    if (this.isBusy() || !composition) {
      return false;
    }

    if (!this.nudge) {
      const move = createNudge(
        this.get().canvasElements,
        this.getSelectedElementIds(),
        getResolution(composition.aspectRatio),
      );

      if (!move) {
        return false;
      }

      this.nudge = { move, offset: { x: 0, y: 0 } };
    }

    const nudge = this.nudge;

    nudge.offset = { x: nudge.offset.x + dx, y: nudge.offset.y + dy };
    this.applyGestureOutput(nudge.move(nudge.offset));

    clearTimeout(nudge.commitTimeoutId);
    nudge.commitTimeoutId = setTimeout(this.commitNudge, NUDGE_COMMIT_DELAY_MS);

    return true;
  };

  // Shortcut actions return whether they were used.
  deleteSelection = () => {
    const editorService = this.prepareSelectionAction();

    if (!editorService) {
      return false;
    }

    const selectedIds = this.getSelectedElementIds();
    const composition = this.editorStore?.getState().video?.compositionJson;

    for (const { element, sceneType } of getAllElements(composition)) {
      if (selectedIds.includes(element.externalId)) {
        editorService.deleteElement(element.externalId, sceneType, false);
      }
    }

    editorService.syncVideoDebounced();

    return true;
  };

  duplicateSelection = () => {
    const editorService = this.prepareSelectionAction();

    if (!editorService) {
      return false;
    }

    const selectedIds = this.getSelectedElementIds();
    const copyIds: string[] = [];

    this.updateSelectedScenes(editorService, (elements) => {
      const duplicated = getDuplicatedElements(elements, selectedIds);

      copyIds.push(...duplicated.copies.map(({ externalId }) => externalId));

      return duplicated.elements;
    });
    this.setSelectedElementIds(copyIds);
    this.syncPanelWithSelection();

    return true;
  };

  copySelection = () => {
    if (!this.prepareSelectionAction()) {
      return false;
    }

    const composition = this.editorStore?.getState().video?.compositionJson;

    if (!composition) {
      return false;
    }

    const selectedIds = this.getSelectedElementIds();
    const { canvasElements } = this.get();

    this.clipboard = {
      resolution: getResolution(composition.aspectRatio),
      entries: getAllElements(composition)
        .filter(({ element }) => selectedIds.includes(element.externalId))
        .map(({ element, sceneType }) => {
          const box = canvasElements.find(
            ({ externalId }) => externalId === element.externalId,
          )?.box;

          return {
            element: structuredClone(element),
            sceneType,
            size: box ?? ("size" in element ? element.size : { w: 0, h: 0 }),
          };
        }),
    };

    return true;
  };

  // Pastes the copied elements into their scenes and selects them.
  pasteElements = () => {
    const editorService = this.editorStore?.getState().service;
    const clipboard = this.clipboard;

    if (!editorService || this.isBusy() || !clipboard) {
      return false;
    }

    this.commitNudge();

    const composition = this.editorStore?.getState().video?.compositionJson;

    if (!composition) {
      return false;
    }

    const { aspectRatio } = composition;
    const resolution = getResolution(aspectRatio);
    const pastedIds: string[] = [];

    // Each add puts the element in front, so add in reverse to keep the copied order.
    for (const entry of [...clipboard.entries].reverse()) {
      const { element, sceneType } = entry;

      if (sceneType !== "mainScene" && !composition[sceneType]?.[aspectRatio]) {
        continue;
      }

      const copy = createElementCopy(
        element,
        getPastePosition(entry, clipboard.resolution, resolution),
      );

      editorService.addElementToComposition(
        copy,
        aspectRatio,
        sceneType,
        false,
      );
      pastedIds.unshift(copy.externalId);
    }

    this.setSelectedElementIds(pastedIds);
    this.syncPanelWithSelection();

    return true;
  };

  // Selects the elements visible at the current frame.
  selectAll = () => {
    const ids = this.get().canvasElements.map(({ externalId }) => externalId);

    if (this.isBusy() || ids.length === 0) {
      return false;
    }

    this.commitNudge();
    this.setSelectedElementIds(ids);
    this.syncPanelWithSelection();

    return true;
  };

  // Cancels a press or nudge, otherwise exits text editing, otherwise clears the selection.
  escape = () => {
    if (this.press) {
      this.endPress();
      return true;
    }

    if (this.nudge) {
      this.cancelNudge();
      return true;
    }

    if (this.get().editingTextId) {
      this.stopTextEditing();
      return true;
    }

    if (this.getSelectedElementIds().length > 0) {
      this.clearSelection();
      return true;
    }

    return false;
  };

  // Only for a single image, video or rectangle.
  makeSelectionFullscreen = () => {
    const editorService = this.prepareSelectionAction();
    const composition = this.editorStore?.getState().video?.compositionJson;
    const selectedIds = this.getSelectedElementIds();

    if (!editorService || !composition || selectedIds.length !== 1) {
      return false;
    }

    const element = getAllElements(composition).find(
      ({ element }) => element.externalId === selectedIds[0],
    )?.element;

    if (!element || !("size" in element)) {
      return false;
    }

    const box = getFullscreenBox(
      element,
      getResolution(composition.aspectRatio),
    );

    editorService.updateElement(
      element.externalId,
      (element) => {
        element.position.x = box.x;
        element.position.y = box.y;

        if ("size" in element) {
          element.size.w = box.w;
          element.size.h = box.h;
        }
      },
      true,
      false,
    );

    return true;
  };

  alignSelection = (alignType: AlignType) => {
    const editorService = this.prepareSelectionAction();
    const composition = this.editorStore?.getState().video?.compositionJson;

    if (!editorService || !composition) {
      return false;
    }

    const selectedIds = this.getSelectedElementIds();
    const offsets = getAlignOffsets(
      this.get().canvasElements.filter(({ externalId }) =>
        selectedIds.includes(externalId),
      ),
      alignType,
      getResolution(composition.aspectRatio),
    );

    for (const [externalId, offset] of Object.entries(offsets)) {
      editorService.updateElement(
        externalId,
        (element) => {
          element.position.x += offset.x;
          element.position.y += offset.y;
        },
        false,
        false,
      );
    }

    editorService.syncVideoDebounced();

    return true;
  };

  reorderSelection = (ordering: ElementOrdering) => {
    const editorService = this.prepareSelectionAction();

    if (!editorService) {
      return false;
    }

    const selectedIds = this.getSelectedElementIds();

    this.updateSelectedScenes(editorService, (elements) =>
      getReorderedElements(elements, selectedIds, ordering),
    );

    return true;
  };

  toggleSelectionVisibility = () => {
    const editorService = this.prepareSelectionAction();

    if (!editorService) {
      return false;
    }

    editorService.toggleElementsVisibility(this.getSelectedElementIds());

    return true;
  };

  // One selected element opens in the overlays tab; several close the element editor.
  private syncPanelWithSelection = () => {
    const editorState = this.editorStore?.getState();
    const selectedIds = this.getSelectedElementIds();

    if (!editorState) {
      return;
    }

    if (selectedIds.length > 1) {
      if (editorState.overlaysTabState.currentElement) {
        editorState.service.updateOverlaysTabState((state) => {
          state.currentElement = null;
        });
      }
      return;
    }

    const selected = getAllElements(editorState.video?.compositionJson).find(
      ({ element }) => element.externalId === selectedIds[0],
    );

    if (!selected) {
      return;
    }

    const { element, sceneType } = selected;

    editorState.service.updateOverlaysTabState((state) => {
      state.currentTab = sceneType === "mainScene" ? "elements" : sceneType;
      state.currentElement = { externalId: element.externalId, sceneType };
    });
    editorState.service.updateEditorPanelState((state) => {
      state.currentTab = "overlays";
    });
  };

  // Rewrites each scene that has selected elements, then syncs once.
  private updateSelectedScenes = (
    editorService: EditorService,
    update: (elements: ElementDto[]) => ElementDto[],
  ) => {
    const composition = this.editorStore?.getState().video?.compositionJson;
    const selectedIds = this.getSelectedElementIds();

    for (const sceneType of SceneTypeDto.options) {
      const elements = getElementsByScene(composition, sceneType);

      if (elements.some(({ externalId }) => selectedIds.includes(externalId))) {
        editorService.setSceneElements(sceneType, update(elements), false);
      }
    }

    editorService.syncVideoDebounced();
  };

  // Returns the editor service when a selection action can run, after committing a nudge
  // so the action sees the nudged positions.
  // Keyboard actions wait while a press is active or text is being edited.
  private isBusy = () =>
    this.press !== null || this.get().editingTextId !== null;

  private prepareSelectionAction = () => {
    const editorService = this.editorStore?.getState().service;

    if (
      !editorService ||
      this.isBusy() ||
      this.getSelectedElementIds().length === 0
    ) {
      return null;
    }

    this.commitNudge();

    return editorService;
  };

  commitNudge = () => {
    if (!this.nudge) {
      return;
    }

    clearTimeout(this.nudge.commitTimeoutId);
    // Cleared before committing so the commit isn't seen as an outside change.
    this.nudge = null;
    this.commitDrafts();
    this.resetTemporaryState();
  };

  private cancelNudge = () => {
    if (!this.nudge) {
      return;
    }

    clearTimeout(this.nudge.commitTimeoutId);
    this.nudge = null;
    this.resetTemporaryState();
  };

  private endPress = () => {
    this.press = null;
    this.resetTemporaryState();
  };

  private resetTemporaryState = () => {
    this.set((state) => {
      state.marquee = null;
      state.drafts = {};
      state.guidelines = [];
      state.activeHandle = null;
    });
  };

  private commitDrafts = () => {
    const editorService = this.editorStore?.getState().service;
    const { drafts } = this.get();

    if (!editorService || Object.keys(drafts).length === 0) {
      return;
    }

    for (const [externalId, draft] of Object.entries(drafts)) {
      editorService.updateElement(
        externalId,
        (element) => {
          element.position.x = draft.x;
          element.position.y = draft.y;

          if ("size" in element) {
            element.size.w = draft.w;
            element.size.h = draft.h;
          }

          if (element.type === "TEXT" && draft.fontSize) {
            element.typography.fontSize = draft.fontSize;
          }

          if (element.type === "TEXT" && draft.text !== undefined) {
            element.text = draft.text;
          }
        },
        false,
        false,
      );
    }

    editorService.syncVideoDebounced();
  };

  private drag = (press: Press, modifiers: Modifiers) => {
    if (press.gesture.drag) {
      this.applyGestureOutput(press.gesture.drag(press.lastPoint, modifiers));
    }
  };

  private applyGestureOutput = ({
    selectedIds,
    marquee,
    drafts,
    guidelines,
  }: GestureOutput) => {
    if (
      selectedIds &&
      !haveSameMembers(selectedIds, this.getSelectedElementIds())
    ) {
      this.setSelectedElementIds(selectedIds);
    }

    if (marquee) {
      this.set((state) => {
        state.marquee = marquee;
      });
    }

    if (drafts) {
      this.set((state) => {
        state.drafts = drafts;
      });
    }

    if (guidelines) {
      this.set((state) => {
        state.guidelines = guidelines;
      });
    }
  };

  clearSelection = () => {
    this.setSelectedElementIds([]);
  };

  private getSelectedElementIds = () => {
    return this.editorStore?.getState().selectedElementIds ?? [];
  };

  private setSelectedElementIds = (elementExternalIds: string[]) => {
    this.editorStore
      ?.getState()
      .service.setSelectedElementIds(elementExternalIds);
  };

  // A press, nudge or text edit is based on the composition it started with, so any other
  // change cancels it.
  private handleCompositionChange = (
    composition: CompositionDto | undefined,
    previousComposition: CompositionDto | undefined,
  ) => {
    if (this.press) {
      this.endPress();
    }

    this.cancelNudge();
    this.cancelTextEditing();

    const selectedIds = this.getSelectedElementIds();

    if (composition?.aspectRatio !== previousComposition?.aspectRatio) {
      if (selectedIds.length > 0) {
        this.clearSelection();
      }
      return;
    }

    const elementIds = new Set(
      getAllElements(composition).map(({ element }) => element.externalId),
    );
    const existingIds = selectedIds.filter((id) => elementIds.has(id));

    if (existingIds.length !== selectedIds.length) {
      this.setSelectedElementIds(existingIds);
    }
  };

  private syncCanvasState = () => {
    this.loadFonts();
    this.refreshCanvasElements();
  };

  private getFont = (fontExternalId: string) => {
    return this.editorStore
      ?.getState()
      .service.getFontByExternalId(fontExternalId);
  };

  // It is fine to call loadFonts repeatedly
  // because loadCustomFont is a no-op if the font is already loaded.
  private loadFonts = () => {
    const composition = this.editorStore?.getState().video?.compositionJson;

    for (const { element } of getAllElements(composition)) {
      if (element.type !== "TEXT") {
        continue;
      }

      const { fontExternalId, fontWeight, fontStyle } = element.typography;
      const font = this.getFont(fontExternalId);

      if (font) {
        loadCustomFont(font, fontWeight, fontStyle).catch(() => {});
      }
    }
  };

  private refreshCanvasElements = () => {
    const editorState = this.editorStore?.getState();
    const composition = editorState?.video?.compositionJson;

    if (!editorState || !composition) {
      this.set((state) => {
        state.canvasElements = [];
      });
      return;
    }

    const timings = getCompositionTimings(
      composition,
      editorState.animatedImages,
    );
    const frame = this.get().frame;
    const canvasElements: CanvasElement[] = [];

    for (const { element, sceneType } of getAllElements(composition)) {
      if (!this.isVisibleAtFrame(element, sceneType, timings, frame)) {
        continue;
      }

      const box = getElementBox(element, this.measureTextElement);

      if (box) {
        canvasElements.push({ externalId: element.externalId, box });
      }
    }

    this.set((state) => {
      state.canvasElements = canvasElements;
    });
  };

  private isVisibleAtFrame = (
    element: ElementDto,
    sceneType: SceneTypeDto,
    timings: ReturnType<typeof getCompositionTimings>,
    frame: number,
  ) => {
    const timing = findElementTiming(timings, element.externalId, sceneType);

    return (
      timing !== null &&
      timing !== undefined &&
      frame >= timing.globalStartAtFrame &&
      frame <= timing.globalEndAtFrame
    );
  };
}
